import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Navbar from "../../../components/Navbar";
import API from "../../../api/api";
import SearchableSelect from "../../../components/common/SearchableSelect";
import { toastMessageAlert } from "../../../utils/alert";

const normalizeGenderToken = (value) => {
  const raw = String(value || "").toLowerCase().trim();
  if (raw === "male" || raw === "men" || raw === "m") return "male";
  if (raw === "female" || raw === "women" || raw === "f") return "female";
  return "all";
};
const toUniqueClassificationIds = (value) =>
  Array.from(
    new Set(
      (Array.isArray(value) ? value : [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0)
        .map((id) => String(id))
    )
  );
const generatedSheetClassificationText = (row) => {
  const names = (Array.isArray(row?.classification_names) ? row.classification_names : [])
    .map((name) => String(name || "").trim())
    .filter(Boolean);
  const ids = toUniqueClassificationIds(row?.classification_ids);
  const includeUnclassified = Boolean(row?.include_unclassified);
  const base = names.length
    ? names.join(" + ")
    : (ids.length ? ids.map((id) => `Class ${id}`).join(" + ") : "All classifications");
  return includeUnclassified ? `${base} + Missed / New Player` : base;
};
const pickGeneratedSheetForResults = (rows, games, activeGender) => {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return null;
  const availableGameIds = new Set(
    (Array.isArray(games) ? games : [])
      .map((g) => String(g?.id || "").trim())
      .filter(Boolean)
  );
  const filteredByGame = list.filter((row) => {
    const gameId = Number(row?.game_id || 0);
    if (gameId <= 0) return true;
    if (!availableGameIds.size) return false;
    return availableGameIds.has(String(gameId));
  });
  if (!filteredByGame.length) return null;
  const wantedGender = normalizeGenderToken(activeGender);
  if (wantedGender === "all") {
    return filteredByGame.find((row) => normalizeGenderToken(row?.gender) === "all") || filteredByGame[0];
  }
  return (
    filteredByGame.find((row) => normalizeGenderToken(row?.gender) === wantedGender) ||
    filteredByGame.find((row) => normalizeGenderToken(row?.gender) === "all") ||
    filteredByGame[0]
  );
};
const formatGeneratedUpdatedAt = (value) => {
  const date = new Date(String(value || ""));
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const TeamManager = () => {
  const navigate = useNavigate();
  const { event_id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialGender = useMemo(() => {
    const g = (searchParams.get("gender") || "all").toLowerCase();
    return ["all", "male", "female"].includes(g) ? g : "all";
  }, [searchParams]);
  const [gender, setGender] = useState(initialGender);
  const date = searchParams.get("date") || "";
  const source = String(searchParams.get("source") || "").toLowerCase();
  const gameIdFromQuery = searchParams.get("game_id") || "";
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [games, setGames] = useState([]);
  const [eventSportName, setEventSportName] = useState("");
  const [selectedGameId, setSelectedGameId] = useState("");
  const [selectedClassificationId, setSelectedClassificationId] = useState("");
  const [generatedMixedClassificationIds, setGeneratedMixedClassificationIds] = useState([]);
  const [generatedMixedLabel, setGeneratedMixedLabel] = useState("");
  const [appliedGeneratedSheet, setAppliedGeneratedSheet] = useState(null);
  const [newTeamName, setNewTeamName] = useState("");
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [message, setMessage] = useState("");
  const gameDayContextQuery = useMemo(() => {
    const q = new URLSearchParams();
    if (date) q.set("date", date);
    if (event_id) q.set("event_id", String(event_id));
    q.set("gender", gender || "all");
    return q.toString();
  }, [date, event_id, gender]);
  const gameEngineContextQuery = useMemo(() => {
    const q = new URLSearchParams(gameDayContextQuery);
    const activeGameId = selectedGameId || gameIdFromQuery;
    if (activeGameId && String(activeGameId) !== "all") {
      q.set("game_id", String(activeGameId));
    }
    return q.toString();
  }, [gameDayContextQuery, selectedGameId, gameIdFromQuery]);
  const backTarget =
    source === "game-engine"
      ? `/admin/game-engine?${gameEngineContextQuery}`
      : `/admin/game-day?${gameDayContextQuery}`;
  const backLabel = source === "game-engine" ? "Back to Game Engine" : "Back to Game Day";
  const activeGenderLabel = gender === "male" ? "Male" : gender === "female" ? "Female" : "All";

  useEffect(() => {
    if (initialGender !== gender) {
      setGender(initialGender);
    }
  }, [initialGender, gender]);

  useEffect(() => {
    if (!message) return;
    toastMessageAlert(message, { timer: 2400 });
  }, [message]);

  useEffect(() => {
    let alive = true;
    if (!event_id) return undefined;

    const loadContext = async () => {
      try {
        const [contextResult, generatedResult] = await Promise.allSettled([
          API.fetchResultEventContext(event_id),
          API.fetchGeneratedScoreSheets({ event_id: Number(event_id) }),
        ]);
        if (!alive) return;

        const ctx =
          contextResult.status === "fulfilled"
            ? (contextResult.value?.data || contextResult.value || {})
            : {};
        setEventSportName(String(ctx?.event?.sport_name || ""));
        const list = Array.isArray(ctx?.games) ? ctx.games : [];
        const teamGames = list.filter((g) => {
          const t = String(g.game_type || "")
            .toUpperCase()
            .replace(/\s*\/\s*/g, "_")
            .replace(/\s+/g, "_");
          const name = String(g.game_name || "").toLowerCase();
          const narration = String(g.game_narration || "").toLowerCase();
          const isTeamBracket = t === "BRACKET" && /(doubles|pairs|mixed|team)/.test(`${name} ${narration}`);
          const isRelay = /(relay|4x)/.test(`${name} ${narration}`);
          return t === "TEAM_SPORT" || isTeamBracket || isRelay;
        });
        const finalList = teamGames.length ? teamGames : list;
        setGames(finalList);

        const rows = generatedResult.status === "fulfilled" && Array.isArray(generatedResult.value)
          ? generatedResult.value
          : [];
        const picked = pickGeneratedSheetForResults(rows, finalList, gender);
        if (!picked) {
          if (gameIdFromQuery && finalList.some((g) => String(g.id) === String(gameIdFromQuery))) {
            setSelectedGameId(String(gameIdFromQuery));
          } else {
            setSelectedGameId("");
          }
          setSelectedClassificationId("");
          setGeneratedMixedClassificationIds([]);
          setGeneratedMixedLabel("");
          setAppliedGeneratedSheet(null);
          return;
        }

        const generatedClassIds = toUniqueClassificationIds(picked?.classification_ids);
        const scopedGameIds = new Set(finalList.map((game) => String(game?.id || "").trim()).filter(Boolean));
        const preferredGameId =
          picked?.game_id && scopedGameIds.has(String(picked.game_id)) ? String(picked.game_id) : "";
        const selectedGameName =
          finalList.find((game) => String(game.id) === String(preferredGameId))?.game_name ||
          (preferredGameId ? `Game ${preferredGameId}` : "All Games");
        const combinedLabel = generatedSheetClassificationText(picked);

        setSelectedGameId(preferredGameId || (gameIdFromQuery && finalList.some((g) => String(g.id) === String(gameIdFromQuery))
          ? String(gameIdFromQuery)
          : ""));
        if (generatedClassIds.length >= 2) {
          setSelectedClassificationId("__mixed__");
          setGeneratedMixedClassificationIds(generatedClassIds);
          setGeneratedMixedLabel(combinedLabel);
        } else if (generatedClassIds.length === 1) {
          setSelectedClassificationId(generatedClassIds[0]);
          setGeneratedMixedClassificationIds([]);
          setGeneratedMixedLabel("");
        } else {
          setSelectedClassificationId("");
          setGeneratedMixedClassificationIds([]);
          setGeneratedMixedLabel("");
        }
        setAppliedGeneratedSheet({
          id: picked?.id ?? null,
          mode: generatedClassIds.length >= 2 ? "Combined Categories" : "Individual Categories",
          gameName: selectedGameName,
          categoryText: combinedLabel,
          totalPlayers: Number.isFinite(Number(picked?.total_players)) ? Number(picked.total_players) : null,
          updatedAt: formatGeneratedUpdatedAt(picked?.updated_at || picked?.created_at),
        });
      } catch (_error) {
        if (!alive) return;
        setEventSportName("");
        setGames([]);
        setSelectedGameId("");
      }
    };

    loadContext();
    return () => {
      alive = false;
    };
  }, [event_id, gameIdFromQuery, gender]);

  useEffect(() => {
    if (!event_id) return;
    API.fetchEventPlayers(event_id, { gender: gender === "all" ? undefined : gender })
      .then((res) => {
        const rows = res?.data || res || [];
        const allRows = Array.isArray(rows) ? rows : [];
        setPlayers(allRows);
      })
      .catch(() => setPlayers([]));

    API.listTeams(event_id, selectedGameId ? { game_id: selectedGameId } : {})
      .then((res) => setTeams(res?.data || res || []))
      .catch(() => setTeams([]));
  }, [event_id, selectedGameId, gender]);

  const classificationOptions = [
    ...new Map(
      players
        .filter((p) => p.classification_id)
        .map((p) => [String(p.classification_id), p.classification_name || `Class ${p.classification_id}`])
    ).entries(),
  ];

  const selectedGameObj = games.find((g) => String(g.id) === String(selectedGameId)) || null;
  const isRelaySelected = /(relay|4x)/.test(
    `${String(selectedGameObj?.game_name || "").toLowerCase()} ${String(selectedGameObj?.game_narration || "").toLowerCase()}`
  );
  const isSwimmingRelaySelected =
    String(eventSportName || "").toLowerCase().includes("swim") &&
    isRelaySelected;
  const activeClassificationIds = useMemo(() => {
    if (selectedClassificationId === "__mixed__") {
      return generatedMixedClassificationIds.map((id) => String(id));
    }
    if (selectedClassificationId) {
      return [String(selectedClassificationId)];
    }
    return [];
  }, [selectedClassificationId, generatedMixedClassificationIds]);

  const filteredPlayers = players.filter((p, idx, arr) => {
    const alreadyAssignedElsewhere = teams.some((team) =>
      (Array.isArray(team.members) ? team.members : []).some((m) => Number(m.player_id) === Number(p.player_id))
    );
    if (alreadyAssignedElsewhere) return false;

    if (activeClassificationIds.length && !activeClassificationIds.includes(String(p.classification_id || ""))) {
      return false;
    }
    if (isRelaySelected) {
      const firstIndex = arr.findIndex(
        (x) => String(x.registration_id) === String(p.registration_id)
      );
      return firstIndex === idx;
    }

    if (selectedGameId && String(p.game_id) !== String(selectedGameId)) return false;
    return true;
  });
  const activeTeamId = selectedTeam || teams[0]?.id;
  const activeTeam = teams.find((t) => String(t.id) === String(activeTeamId)) || null;
  const relayTeamFull = isSwimmingRelaySelected && Number(activeTeam?.member_count || 0) >= 4;

  const createTeam = async () => {
    if (!newTeamName || !selectedGameId) return;
    try {
      await API.createTeam(event_id, {
        team_name: newTeamName,
        game_id: Number(selectedGameId),
      });
      setNewTeamName("");
      setMessage("Team created.");
      const res = await API.listTeams(event_id, { game_id: selectedGameId });
      setTeams(res?.data || res || []);
    } catch (err) {
      setMessage(err?.message || "Failed to create team.");
    }
  };

  const addMember = async (teamId, regId) => {
    if (!teamId) {
      setMessage("Select a team first.");
      return;
    }
    try {
      await API.addTeamMember(teamId, { registration_id: regId });
      setMessage("Member added.");
      const res = await API.listTeams(event_id, selectedGameId ? { game_id: selectedGameId } : {});
      setTeams(res?.data || res || []);
    } catch (err) {
      setMessage(err?.message || "Failed to add member.");
    }
  };

  const handleGenderChange = (nextGender) => {
    const normalized = ["all", "male", "female"].includes(nextGender) ? nextGender : "all";
    setGender(normalized);
    const q = new URLSearchParams(searchParams);
    q.set("gender", normalized);
    setSearchParams(q, { replace: true });
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto p-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-gray-900">Team Manager</h1>
            <div className="flex gap-2 items-center">
              <label className="text-sm text-gray-700">
                Gender
                <select
                  value={gender}
                  onChange={(e) => handleGenderChange(e.target.value)}
                  className="ml-2 rounded border border-gray-300 px-2 py-1"
                >
                  <option value="all">All</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </label>
              <button
                onClick={() => navigate(backTarget)}
                className="text-sm px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300"
              >
                {backLabel}
              </button>
            </div>
          </div>
          <p className="text-xs text-blue-700 mb-3">Active Filter: Gender = {activeGenderLabel}</p>

          {message ? <div className="mb-3 text-sm bg-gray-100 p-3 rounded-lg">{message}</div> : null}

          <div className="bg-white rounded-xl shadow p-5 mb-4">
            <div className="grid md:grid-cols-4 gap-2">
              <SearchableSelect
                value={selectedGameId}
                onChange={(e) => {
                  setSelectedGameId(e.target.value);
                  setSelectedClassificationId("");
                  setGeneratedMixedClassificationIds([]);
                  setGeneratedMixedLabel("");
                }}
                className="border p-2 rounded w-full"
              >
                <option value="">All Games</option>
                {games.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.game_name}
                  </option>
                ))}
              </SearchableSelect>
              <SearchableSelect
                value={selectedClassificationId}
                onChange={(e) => setSelectedClassificationId(e.target.value)}
                disabled={isRelaySelected}
                className="border p-2 rounded w-full"
              >
                <option value="">All Classifications</option>
                {generatedMixedClassificationIds.length >= 2 ? (
                  <option value="__mixed__">{generatedMixedLabel || "Mixed Classifications"}</option>
                ) : null}
                {classificationOptions.map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </SearchableSelect>
              <input
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                className="border p-2 rounded w-full md:col-span-2"
                placeholder="Team name"
              />
              <button onClick={createTeam} className="bg-blue-600 text-white px-4 py-2 rounded">
                Create Team
              </button>
            </div>
            {isSwimmingRelaySelected ? (
              <p className="text-xs text-amber-700 mt-2">
                Swimming relay rule: each team must have exactly 4 players.
              </p>
            ) : null}
            {appliedGeneratedSheet ? (
              <div className="mt-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-900">
                <span className="font-semibold">Applied from generated score sheet:</span>{" "}
                {appliedGeneratedSheet.mode} | {appliedGeneratedSheet.gameName} | {appliedGeneratedSheet.categoryText}
                {appliedGeneratedSheet.totalPlayers !== null ? ` | Total Players: ${appliedGeneratedSheet.totalPlayers}` : ""}
                {appliedGeneratedSheet.updatedAt && appliedGeneratedSheet.updatedAt !== "-"
                  ? ` | Updated: ${appliedGeneratedSheet.updatedAt}`
                  : ""}
              </div>
            ) : null}
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow p-4">
              <h2 className="font-semibold mb-3">Teams</h2>
              <ul className="space-y-2">
                {teams.map((t) => (
                  <li key={t.id} className="border rounded p-2 flex justify-between items-center">
                    <div>
                      <div>{t.team_name}</div>
                      <div className="text-xs text-gray-500">
                        Members: {t.member_count || 0}
                        {isSwimmingRelaySelected ? " / 4" : ""}
                      </div>
                    </div>
                    <button onClick={() => setSelectedTeam(t.id)} className="text-sm text-blue-700">
                      Select
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow p-4 lg:col-span-2">
              <h2 className="font-semibold mb-3">Players</h2>
              <ul className="space-y-2 max-h-[420px] overflow-auto">
                {filteredPlayers.map((p) => (
                  <li key={p.registration_id} className="border rounded p-2 flex justify-between items-center">
                    <span>
                      {p.name}{" "}
                      <span className="text-xs text-gray-500">
                        ({isRelaySelected ? "Any registered game" : p.game_name})
                      </span>
                    </span>
                    <button
                      onClick={() => addMember(selectedTeam || teams[0]?.id, p.registration_id)}
                      disabled={relayTeamFull}
                      className="text-sm bg-green-600 text-white px-3 py-1 rounded disabled:bg-gray-400"
                    >
                      {relayTeamFull ? "Team Full" : "Add to Team"}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TeamManager;
